import type { FirewallRulesetIssue } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';

/**
 * The structured issue list from a rejected firewall write (#1760).
 *
 * One renderer for both sources — the local pre-flight in
 * `lib/firewallRuleDraft.ts` emits the engine's own codes and `at` paths, so an
 * operator never has to learn that some problems are reported by the browser
 * and some by the gateway. The full list is shown, never just the first: a
 * draft with four mistakes should cost one round trip, not four.
 *
 * `code` and `at` are rendered verbatim rather than translated. They are the
 * engine's identifiers, they appear in the gateway's own logs, and a localized
 * paraphrase would break the one string an operator can search for.
 */
export function FirewallIssueList({
  issues,
  testId,
}: {
  readonly issues: readonly FirewallRulesetIssue[];
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  if (issues.length === 0) return null;
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-bad-line bg-bad-dim/30 p-3"
      role="alert"
      data-testid={testId}
    >
      <p className="m-0 text-label font-medium text-bad">
        {t('firewall_rulesets.issues.title', { count: issues.length })}
      </p>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {issues.map((issue, index) => (
          <li
            key={`${issue.code}:${issue.at ?? ''}:${String(index)}`}
            className="flex flex-col gap-0.5"
          >
            <span className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-raised px-1.5 py-0.5 font-mono text-micro text-paper-mid">
                {issue.code}
              </span>
              {issue.at === undefined ? null : (
                <span className="font-mono text-micro text-paper-low">{issue.at}</span>
              )}
              {issue.ruleId === undefined ? null : (
                <span className="font-mono text-micro text-paper-low">{issue.ruleId}</span>
              )}
            </span>
            <span className="text-label text-paper-mid">{issue.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
